/**
 * searchIntelligence — "¿cómo llegó buscando a la creadora?"
 *
 * Extrae, cuando es posible, el buscador y la QUERY REAL con la que el visitante
 * llegó, y la clasifica en una intención accionable (marca, marca+geo, adulto,
 * comercial…).
 *
 * ⚠️  LIMITACIÓN REAL, NO ES UN BUG:
 * Google dejó de mandar la query en el referrer en **octubre de 2011** (SSL
 * search / "not provided"). Hoy el referrer de una búsqueda orgánica de Google
 * llega como `https://www.google.com/` pelado — sin `?q=`. DuckDuckGo hace lo
 * mismo por diseño (referrer policy estricta). Por eso, para esos dos motores
 * detectamos `engine` pero `query` queda en `null` con
 * `queryConfidence: "none"`. NO existe forma client-side de recuperarla; la
 * única fuente real para Google es Search Console (server-side, agregado) o
 * `utm_term` cuando Google Ads lo puebla con el search term.
 * Si alguien "arregla" esto parseando el referrer de Google, va a estar
 * leyendo un querystring que nunca llega.
 *
 * Fuentes de query por orden de confianza:
 *   1. exact  → utm_term, o params de búsqueda en la URL de aterrizaje,
 *               o referrer de buscadores que SÍ pasan la query (Bing, Yahoo,
 *               Yandex, Baidu, Ecosia, Brave, Startpage, AOL, Ask).
 *   2. none   → Google / DuckDuckGo orgánico sin utm_term.
 *
 * Uso:
 *   import { searchIntelFunnelProps } from "@/lib/searchIntelligence";
 *   // props planos search_* que se spreadean en cada funnel_event
 *
 * El módulo es multi-marca: el nombre/handle contra el que se compara sale de
 * `@/lib/brandConfig`, nunca hardcodeado.
 */

import { BRAND_PERSON_NAME, BRAND_NAME, BRAND_HANDLE } from "@/lib/brandConfig";

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type SearchIntent =
  | "brand"
  | "brand_geo"
  | "brand_social"
  | "adult"
  | "commercial"
  | "generic"
  | "unknown";

export interface SearchIntel {
  /** Motor normalizado: "google" | "bing" | "duckduckgo" | … | null */
  engine: string | null;
  /** Query real cuando se pudo recuperar. null si el motor no la pasa. */
  query: string | null;
  /** "exact" = la query viajó de verdad. "none" = hay motor pero no query. */
  queryConfidence: "exact" | "inferred" | "none";
  /** Clasificación accionable de la query. "unknown" si no hay query. */
  intent: SearchIntent;
  /** true para brand | brand_geo | brand_social */
  isBranded: boolean;
  /** Modificador geográfico que matcheó dentro de la query (si hubo). */
  geoModifier: string | null;
  raw: Record<string, string | undefined>;
}

// ─── Normalización ────────────────────────────────────────────────────────────

/** lowercase + sin acentos + espacios colapsados. Base de toda comparación. */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[_+]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ─── Motores ──────────────────────────────────────────────────────────────────

/**
 * Cada entrada: cómo reconocer el host y, si el motor pasa la query en el
 * referrer, con qué param. `queryParam: null` = el motor NO la pasa (Google,
 * DuckDuckGo, Qwant) — ver el comentario del encabezado.
 */
const ENGINE_MAP: Array<{
  engine: string;
  test: (host: string) => boolean;
  queryParam: string | null;
}> = [
  // Google: TODOS los TLD (google.com, google.com.ar, google.es, google.co.uk…)
  { engine: "google", test: h => /(^|\.)google\.[a-z]{2,3}(\.[a-z]{2})?$/.test(h), queryParam: null },
  { engine: "bing", test: h => /(^|\.)bing\.com$/.test(h), queryParam: "q" },
  { engine: "duckduckgo", test: h => /(^|\.)duckduckgo\.com$/.test(h), queryParam: null },
  { engine: "yahoo", test: h => /(^|\.)search\.yahoo\.[a-z.]+$|(^|\.)yahoo\.[a-z.]+$/.test(h), queryParam: "p" },
  { engine: "yandex", test: h => /(^|\.)yandex\.[a-z.]+$/.test(h), queryParam: "text" },
  { engine: "baidu", test: h => /(^|\.)baidu\.com$/.test(h), queryParam: "wd" },
  { engine: "ecosia", test: h => /(^|\.)ecosia\.org$/.test(h), queryParam: "q" },
  { engine: "brave", test: h => /(^|\.)search\.brave\.com$/.test(h), queryParam: "q" },
  { engine: "startpage", test: h => /(^|\.)startpage\.com$/.test(h), queryParam: "query" },
  { engine: "qwant", test: h => /(^|\.)qwant\.com$/.test(h), queryParam: null },
  { engine: "aol", test: h => /(^|\.)aol\.[a-z.]+$/.test(h), queryParam: "q" },
  { engine: "ask", test: h => /(^|\.)ask\.com$/.test(h), queryParam: "q" },
];

/** Params de búsqueda que pueden venir en la URL de aterrizaje del sitio. */
const LANDING_QUERY_PARAMS = ["q", "query", "search_query", "text", "p", "wd", "kw"] as const;

// ─── Diccionarios de intención ────────────────────────────────────────────────

/**
 * Modificadores geográficos: provincias/ciudades argentinas relevantes + países
 * hispanos. Normalizados (sin acentos, lowercase) para comparar directo.
 */
const GEO_MODIFIERS = [
  // Argentina — ciudades / provincias
  "san miguel de tucuman",
  "tucuman",
  "buenos aires",
  "caba",
  "capital federal",
  "la plata",
  "mar del plata",
  "cordoba",
  "rosario",
  "santa fe",
  "mendoza",
  "salta",
  "jujuy",
  "san juan",
  "san luis",
  "santiago del estero",
  "catamarca",
  "la rioja",
  "chaco",
  "corrientes",
  "entre rios",
  "formosa",
  "misiones",
  "neuquen",
  "rio negro",
  "chubut",
  "santa cruz",
  "tierra del fuego",
  "la pampa",
  "bariloche",
  "argentina",
  // Países hispanos / mercados cercanos
  "chile",
  "uruguay",
  "paraguay",
  "bolivia",
  "peru",
  "colombia",
  "venezuela",
  "ecuador",
  "mexico",
  "espana",
  "estados unidos",
  "brasil",
];

/** Plataformas sociales — branded + plataforma = intención de "buscar su perfil". */
const SOCIAL_TERMS = [
  "instagram",
  "ig",
  "tiktok",
  "onlyfans",
  "telegram",
  "twitter",
  "x",
  "reels",
  "fotos",
  "videos",
  "youtube",
  "threads",
  "snapchat",
];

/**
 * Términos NSFW. Alineados con la regex `NOINDEX_NSFW` de
 * server/middleware/seoHeaders.ts (desnuda, nude, pornhub, xvideos, erome,
 * filtrados, leaked, onlyfans…). Los términos ambiguos de esa regex que son
 * puramente comerciales (pack, premium, gratis) NO viven acá: caen en
 * `commercial`.
 */
const ADULT_TERMS = [
  "desnuda",
  "desnudo",
  "desnudas",
  "sin ropa",
  "nude",
  "nudes",
  "porn",
  "porno",
  "pornhub",
  "xvideos",
  "xxx",
  "erome",
  "leaked",
  "leak",
  "filtrada",
  "filtrado",
  "filtrados",
  "hot",
  "caliente",
  "tetas",
  "culo",
  "sexo",
  "sexy",
  "erotica",
  "erotico",
];

/** Intención de compra / conversión. */
const COMMERCIAL_TERMS = [
  "precio",
  "precios",
  "comprar",
  "suscripcion",
  "suscripciones",
  "membresia",
  "membresias",
  "cuanto sale",
  "cuanto cuesta",
  "vip",
  "pack",
  "packs",
  "premium",
  "pagar",
  "cuesta",
  "tarifa",
  "planes",
];

// ─── Términos de marca (multi-marca, desde brandConfig) ───────────────────────

/**
 * Tokens de marca contra los que se testea "branded". Se derivan de brandConfig
 * y se agrega la variante sin espacios (así "anafernandez" matchea igual que
 * "ana fernandez"). Nunca hardcodear un nombre acá: el repo se forkea.
 */
function buildBrandTerms(): string[] {
  const seeds = [BRAND_PERSON_NAME, BRAND_NAME, BRAND_HANDLE]
    .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
    .map(normalize)
    .filter(Boolean);

  const terms = new Set<string>();
  for (const seed of seeds) {
    terms.add(seed);
    const collapsed = seed.replace(/\s+/g, "");
    if (collapsed.length >= 4) terms.add(collapsed);
  }
  return [...terms];
}

const BRAND_TERMS = buildBrandTerms();

/** ¿La query (ya normalizada) contiene la marca, con o sin espacios? */
function isBrandedQuery(normalizedQuery: string): boolean {
  if (!normalizedQuery) return false;
  const collapsed = normalizedQuery.replace(/\s+/g, "");
  return BRAND_TERMS.some(term => normalizedQuery.includes(term) || collapsed.includes(term));
}

function findTerm(normalizedQuery: string, terms: string[]): string | null {
  // Recorrido de más largo a más corto para que "san miguel de tucuman" gane
  // sobre "tucuman".
  const ordered = [...terms].sort((a, b) => b.length - a.length);
  for (const term of ordered) {
    if (term.includes(" ")) {
      if (normalizedQuery.includes(term)) return term;
    } else {
      // Palabra completa: evita que "x" matchee dentro de "sexo".
      const re = new RegExp(`(^|\\s)${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|\\s)`);
      if (re.test(normalizedQuery)) return term;
    }
  }
  return null;
}

// ─── Clasificación ────────────────────────────────────────────────────────────

export function classifySearchQuery(query: string | null): {
  intent: SearchIntent;
  isBranded: boolean;
  geoModifier: string | null;
} {
  if (!query || !query.trim()) {
    return { intent: "unknown", isBranded: false, geoModifier: null };
  }

  const q = normalize(query);
  const branded = isBrandedQuery(q);
  const geoModifier = findTerm(q, GEO_MODIFIERS);

  if (branded) {
    if (geoModifier) return { intent: "brand_geo", isBranded: true, geoModifier };
    if (findTerm(q, SOCIAL_TERMS)) return { intent: "brand_social", isBranded: true, geoModifier: null };
    return { intent: "brand", isBranded: true, geoModifier: null };
  }

  // Adult antes que commercial: "pack desnuda" es adult, no comercial.
  if (findTerm(q, ADULT_TERMS)) return { intent: "adult", isBranded: false, geoModifier };
  if (findTerm(q, COMMERCIAL_TERMS)) return { intent: "commercial", isBranded: false, geoModifier };
  return { intent: "generic", isBranded: false, geoModifier };
}

// ─── Extracción ───────────────────────────────────────────────────────────────

function resolveEngineFromHost(host: string): { engine: string; queryParam: string | null } | null {
  const h = host.replace(/^www\./, "").toLowerCase();
  for (const entry of ENGINE_MAP) {
    if (entry.test(h)) return { engine: entry.engine, queryParam: entry.queryParam };
  }
  return null;
}

const EMPTY_INTEL: SearchIntel = {
  engine: null,
  query: null,
  queryConfidence: "none",
  intent: "unknown",
  isBranded: false,
  geoModifier: null,
  raw: {},
};

/**
 * Lee la señal de búsqueda del pageview actual (URL + referrer).
 * SSR-safe: devuelve `EMPTY_INTEL` fuera del browser.
 */
export function detectSearchIntel(): SearchIntel {
  if (typeof window === "undefined") return { ...EMPTY_INTEL };

  const params = new URLSearchParams(window.location.search);
  const raw: Record<string, string | undefined> = {};

  // ── Motor por referrer ──────────────────────────────────────────────────
  let engine: string | null = null;
  let referrerQuery: string | null = null;

  try {
    if (document.referrer) {
      const refUrl = new URL(document.referrer);
      const host = refUrl.hostname;
      const hit = resolveEngineFromHost(host);
      if (hit) {
        engine = hit.engine;
        raw.referrer_host = host.replace(/^www\./, "").toLowerCase();
        if (hit.queryParam) {
          // Motores que SÍ pasan la query en el querystring del referrer.
          const value = refUrl.searchParams.get(hit.queryParam);
          if (value && value.trim()) referrerQuery = value.trim();
        }
        // Google / DuckDuckGo / Qwant: queryParam === null → no hay query
        // recuperable (referrer policy). Ver comentario del encabezado.
      }
    }
  } catch {
    /* referrer inválido / opaco → ignorar */
  }

  // ── Motor por click ID (sin referrer útil, p.ej. ads en in-app browser) ──
  if (!engine) {
    if (params.has("gclid") || params.has("gbraid") || params.has("wbraid")) {
      engine = "google";
      raw.search_click_id = params.has("gclid")
        ? "gclid"
        : params.has("gbraid")
          ? "gbraid"
          : "wbraid";
    } else if (params.has("msclkid")) {
      engine = "bing";
      raw.search_click_id = "msclkid";
    }
  }

  // ── Query: utm_term → params de landing → referrer ──────────────────────
  let query: string | null = null;

  const utmTerm = params.get("utm_term")?.trim();
  if (utmTerm) {
    // Google Ads puebla utm_term con el search term real cuando el anunciante
    // usa {keyword} / {searchterm} en el tracking template.
    query = utmTerm;
    raw.utm_term = utmTerm;
  }

  if (!query) {
    for (const key of LANDING_QUERY_PARAMS) {
      const value = params.get(key)?.trim();
      if (value) {
        query = value;
        raw.landing_query_param = key;
        break;
      }
    }
  }

  if (!query && referrerQuery) {
    query = referrerQuery;
    raw.referrer_query_param = "1";
  }

  if (!engine && !query) return { ...EMPTY_INTEL };

  const { intent, isBranded, geoModifier } = classifySearchQuery(query);

  return {
    engine,
    query,
    // "exact" siempre que la query viajó de verdad; "none" cuando hay motor
    // pero el motor no la pasa (Google/DDG orgánico). El nivel "inferred"
    // queda reservado por si alguna vez se reconstruye desde otra señal.
    queryConfidence: query ? "exact" : "none",
    intent,
    isBranded,
    geoModifier,
    raw,
  };
}

// ─── First-touch persistence ──────────────────────────────────────────────────

/**
 * sessionStorage key propia (paralela a `aaf_first_touch_source` de
 * detectTrafficSource.ts). La señal de búsqueda vive en el PRIMER pageview:
 * después el referrer es interno y los params ya se perdieron.
 */
const FIRST_TOUCH_KEY = "aaf_first_touch_search";

/** Señal "fuerte" = al menos hay motor o query. */
function isStrongIntel(s: SearchIntel): boolean {
  return Boolean(s.engine || s.query);
}

/**
 * Devuelve la señal de búsqueda first-touch de la sesión. La congela en el
 * primer pageview con señal; upgradea sólo si lo guardado era vacío.
 */
export function getOrCaptureFirstTouchSearchIntel(): SearchIntel {
  const live = detectSearchIntel();
  if (typeof window === "undefined") return live;

  try {
    const stored = sessionStorage.getItem(FIRST_TOUCH_KEY);
    if (stored) {
      const prev = JSON.parse(stored) as SearchIntel;
      if (!isStrongIntel(prev) && isStrongIntel(live)) {
        sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(live));
        return live;
      }
      return prev;
    }
    if (isStrongIntel(live)) sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(live));
  } catch {
    /* sessionStorage bloqueado (Safari ITP / in-app browser) → usar live */
  }
  return live;
}

/**
 * Props planos para spreadear en CADA funnel_event → Slack, DB y PostHog.
 * Mismo estilo que `trafficSourceFunnelProps()` en detectTrafficSource.ts:
 * se omite todo lo null/undefined para no ensuciar los eventos.
 *
 * Claves:
 *   search_engine            "google" | "bing" | … (motor detectado)
 *   search_query             query real, SÓLO si el motor la pasó
 *   search_query_confidence  "exact" | "none"
 *   search_intent            "brand" | "brand_geo" | "adult" | …
 *   search_is_branded        true si buscó el nombre de la creadora
 *   search_geo_modifier      "tucuman" | "buenos aires" | …
 */
export function searchIntelFunnelProps(): Record<string, unknown> {
  const s = getOrCaptureFirstTouchSearchIntel();
  const out: Record<string, unknown> = {};
  if (!s.engine && !s.query) return out;

  if (s.engine) out.search_engine = s.engine;
  if (s.query) out.search_query = s.query;
  if (s.engine || s.query) out.search_query_confidence = s.queryConfidence;
  if (s.intent && s.intent !== "unknown") out.search_intent = s.intent;
  if (s.isBranded) out.search_is_branded = true;
  if (s.geoModifier) out.search_geo_modifier = s.geoModifier;
  return out;
}
